import { useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowUpRight, Check } from "lucide-react";
import { capabilities, serviceDetails } from "@/data";

const EASE = [0.22, 1, 0.36, 1];

function FadeIn({ children, delay = 0 }) {
  return (
    <motion.div initial={{ opacity: 0, y: 32 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-40px" }} transition={{ duration: 0.7, delay, ease: EASE }}>
      {children}
    </motion.div>
  );
}

export default function ServiceDetail() {
  const { num } = useParams();
  const cap = capabilities.find((c) => c.num === num);
  if (!cap) {
    return (
      <div className="svc-shell" data-testid="service-not-found">
        <div className="svc-inner">
          <a href="/" className="svc-back" data-testid="service-notfound-back"><ArrowLeft size={16} /> Back to website</a>
          <h1>Service not found</h1>
        </div>
      </div>
    );
  }
  const detail = serviceDetails[num];
  const Icon = cap.icon;
  const overview = detail ? detail.overview : [cap.desc];
  const offer = detail ? detail.offer : cap.activities.map((a) => [a, null]);

  return (
    <div className="svc-shell" data-testid="service-detail-page">
      <header className="svc-topbar" data-testid="service-topbar">
        <a href="/" className="svc-back" data-testid="service-back-link"><ArrowLeft size={16} /> Back to website</a>
        <span className="svc-crumb">OUR CAPABILITIES / {cap.num}</span>
      </header>

      <section className="svc-hero">
        <div className="svc-inner svc-hero-grid">
          <FadeIn>
            <div>
              <span className="svc-num" data-testid="service-number">{cap.num}</span>
              <h1 data-testid="service-title">{cap.title}</h1>
              {detail && <p className="svc-tagline" data-testid="service-tagline">{detail.tagline}</p>}
              {cap.highlight && <span className="cap-highlight" data-testid="service-highlight"><strong>{cap.highlight[0]}</strong>{cap.highlight[1]}</span>}
            </div>
          </FadeIn>
          <FadeIn delay={0.12}>
            <div className="svc-photo" data-testid="service-photo"><img src={cap.photo} alt={cap.title} /><Icon size={28} className="svc-photo-icon" /></div>
          </FadeIn>
        </div>
      </section>

      <section className="svc-section">
        <div className="svc-inner">
          <FadeIn><p className="svc-label" data-testid="service-overview-label">OVERVIEW</p></FadeIn>
          {overview.map((para, i) => (
            <FadeIn key={i} delay={0.06 * i}><p className="svc-overview-text" data-testid={`service-overview-${i}`}>{para}</p></FadeIn>
          ))}
        </div>
      </section>

      <section className="svc-section svc-alt">
        <div className="svc-inner">
          <FadeIn><p className="svc-label" data-testid="service-offer-label">WHAT WE OFFER</p></FadeIn>
          <div className="svc-offer-grid" data-testid="service-offer-grid">
            {offer.map(([title, desc], i) => (
              <FadeIn key={title} delay={0.05 * (i % 2)}>
                <div className="svc-offer-item" data-testid={`service-offer-${i}`}>
                  <h3>{title}</h3>
                  {desc && <p>{desc}</p>}
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {detail && (
        <section className="svc-section">
          <div className="svc-inner">
            <FadeIn><p className="svc-label" data-testid="service-process-label">OUR PROCESS</p></FadeIn>
            <ol className="svc-process-list" data-testid="service-process-list">
              {detail.process.map(([title, desc], i) => (
                <FadeIn key={title} delay={0.06 * i}>
                  <li className="svc-process-item" data-testid={`service-process-${i}`}>
                    <span>{String(i + 1).padStart(2, "0")}</span>
                    <div><h3>{title}</h3><p>{desc}</p></div>
                  </li>
                </FadeIn>
              ))}
            </ol>
          </div>
        </section>
      )}

      {detail && (
        <section className="svc-section svc-alt">
          <div className="svc-inner">
            <FadeIn><p className="svc-label" data-testid="service-why-label">WHY CHOOSE US</p></FadeIn>
            <ul className="svc-why-list" data-testid="service-why-list">
              {detail.why.map((item, i) => (
                <FadeIn key={item} delay={0.05 * i}>
                  <li data-testid={`service-why-${i}`}><Check size={18} />{item}</li>
                </FadeIn>
              ))}
            </ul>
          </div>
        </section>
      )}

      <section className="svc-cta" data-testid="service-cta">
        <div className="svc-inner">
          <FadeIn>
            <p className="svc-label svc-label-light">GET STARTED</p>
            <p className="svc-cta-text" data-testid="service-cta-text">
              {detail?.cta || "Tell us about your scope, location and timeline. We'll respond with a clear way forward."}
            </p>
            <div className="svc-cta-actions">
              <a href="/#contact" className="primary-button" data-testid="service-quote-button">Request a Quote <ArrowUpRight size={18} /></a>
              <a href="/#capabilities" className="svc-cta-secondary" data-testid="service-all-button">All capabilities</a>
            </div>
          </FadeIn>
        </div>
      </section>
    </div>
  );
}
